import { PredictiveAnswerTypeDto } from "@advanced-sales-technologies/backend-client/models/PredictiveAnswerTypeDto";

import {
  Answer,
  AnswerType,
  AnswerTypeMap,
  ComponentField,
  DefaultValues,
  FieldType,
  FormConfigField,
  FormValues,
  ValueFor,
} from "@/types/DynamicForm.type";

/**
 * Format a set of raw form values into the array the backend needs.
 */
export type GenericAnswerDTO = {
  target: string;
  answer: { data: Answer };
};
export function formatAnswers<C extends readonly FormConfigField[], V extends FormValues<C>>(
  config: C,
  answers: V,
): GenericAnswerDTO[] {
  return config.flatMap((field): GenericAnswerDTO[] => {
    if (!("name" in field)) return []; // skip display‑only
    const raw = answers[field.name as keyof V] as unknown;

    if (raw == null || (typeof raw === "string" && raw.trim() === "") || (Array.isArray(raw) && raw.length === 0))
      return [];

    if (field.serialize) {
      const data = field.serialize(raw as any);
      return [{ target: field.name, answer: { data } }];
    }

    return Array.isArray(raw)
      ? [
          {
            target: field.name,
            answer: {
              data: {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                values: (raw as any[]).map((o) => ({ id: o.id })),
                type: "MULTIPLE_CHOICE" as PredictiveAnswerTypeDto.MULTIPLE_CHOICE,
              },
            },
          },
        ]
      : [{ target: field.name, answer: { data: { value: raw, type: "TEXT" as PredictiveAnswerTypeDto.TEXT } } }];
  });
}

export function defaultFor(at: AnswerType): AnswerTypeMap[typeof at] {
  switch (at) {
    case "string":
      return ""; // ""
    case "number":
      return 0; // 0
    case "boolean":
      return false; // false
    case "array":
      return []; // []
    case "object":
      return {}; // {}
    case "date":
      return new Date(); // today
  }
}

/**
 * Runtime default generator keyed by **fieldType** (not answerType).
 *
 * The exhaustive switch is checked by TypeScript; adding a new `FieldType`
 * that isn’t handled will raise a compiler error (the `never` check at the
 * end). So we keep a single, DRY source‑of‑truth without a second mapping.
 */
export function defaultForFieldType<F extends FieldType>(ft: F): ValueFor<F> {
  switch (ft) {
    case "input":
    case "textarea":
      return "" as ValueFor<F>; // primitive string

    case "subscriptionSelect":
      return { id: "" } as ValueFor<F>; // single object with id

    case "checkbox":
      return false as ValueFor<F>;

    case "checkboxes":
    case "comboBoxMultiple":
    case "industries":
    case "companies":
    case "regionSelect":
    case "naceSelect":
      return [] as ValueFor<F>; // array defaults

    case "comboBoxSingle":
      return { id: "" } as ValueFor<F>; // object with id, single selection

    case "locationSelect":
      return { country_code: "" } as ValueFor<F>; // object with country_code

    case "header":
    case "warning":
      // display‑only – never used in form state
      return undefined as never;
  }
  /* istanbul ignore next */
  const _exhaustiveCheck: never = ft;
  return _exhaustiveCheck;
}

/**
 * Returns the wording that should appear in the UI for a field.
 * • If it came from the backend question DTO, prefer that.
 * • Otherwise fall back to the static `label` in the form config.
 */
export const getQuestionLabel = (cfg: FormConfigField): string => {
  if ("question" in cfg && cfg.question?.data?.question) {
    return cfg.question?.data.question;
  }
  return cfg.label ?? "";
};

export function buildDefaultValues<C extends readonly FormConfigField[]>(config: C) {
  return Object.fromEntries(
    config
      .filter((f): f is Extract<C[number], ComponentField> => "name" in f)
      .map((f) => [f.name, f.defaultValue ?? defaultForFieldType(f.fieldType)]),
  ) as DefaultValues<C>;
}

/**
 * Best‑effort extraction of an **already‑saved** answer by `target`.
 * Works for campaign or product answer DTOs.
 *
 *   • If the answer has a `value` → returns that value.
 *   • If it has `values` → returns that array.
 *   • Otherwise → `undefined`.
 *
 * The return type is inferred at compile‑time (string, string[], etc.) based
 * on the shape of the underlying `data` object.
 */
export function findAnswer<A extends { target: unknown; answer: { data: unknown } }, T extends A["target"]>(
  answers: readonly A[] | undefined,
  target: T,
) {
  const found = answers?.find((a) => a?.target === target);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const data = found?.answer?.data as any;

  if (!data) return undefined as Extract<undefined, never>;
  if ("value" in data) return data.value as typeof data.value;
  if ("values" in data) return data.values as typeof data.values;
  return undefined as Extract<undefined, never>;
}
